import React, { useState, useEffect } from 'react';
import {
  Box,
  OutlinedInput,
  InputAdornment,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { POLICY_NAV_LABEL } from '../../constants/policyLabels';

/**
 * @param {string} [searchQuery] - Controlled value (when provided, parent controls input)
 * @param {function} [onSearchQueryChange] - Called when input value changes (for controlled mode)
 * @param {function} onSearch - Called when user submits search (click Search or Enter)
 * @param {string} [placeholder] - Placeholder text
 */
const BillsSearchBar = ({
  searchQuery,
  onSearchQueryChange,
  onSearch,
  placeholder = `Search ${POLICY_NAV_LABEL.toLowerCase()}…`,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const shellRadius = isDesktop ? '8px' : '50px';
  const searchButtonRadius = isDesktop ? '0 8px 8px 0' : '0 50px 50px 0';
  const isControlled = searchQuery !== undefined;
  const [internalValue, setInternalValue] = useState(isControlled ? searchQuery : '');
  const value = isControlled ? (searchQuery ?? '') : internalValue;

  useEffect(() => {
    if (isControlled) setInternalValue(searchQuery ?? '');
  }, [isControlled, searchQuery]);

  const setValue = (next) => {
    onSearchQueryChange?.(next);
    if (!isControlled) setInternalValue(next);
  };

  const doSearch = () => {
    const q = (value || '').trim();
    onSearch(q);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        minWidth: isDesktop ? 480 : 300,
        maxWidth: isDesktop ? 736 : 560,
        width: isDesktop ? 736 : '100%',
        bgcolor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: shellRadius,
        overflow: 'hidden',
        height: '43px',
      }}
    >
      <OutlinedInput
        fullWidth
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && doSearch()}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon color="action" />
          </InputAdornment>
        }
        sx={{
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          height: '100%',
        }}
      />
      <Button
        variant="contained"
        onClick={doSearch}
        sx={{
          height: '100%',
          borderRadius: searchButtonRadius,
          textTransform: 'none',
          px: 2,
          bgcolor: 'black',
          marginTop: '0 !important',
          '&:hover': { bgcolor: 'grey.800' },
        }}
      >
        Search
      </Button>
    </Box>
  );
};

export default BillsSearchBar;
